import { AxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { notificationService, smartFunctionService } from '../services'
import workspaceService from '../services/workspaceService'
import { RootState } from '../store'
import { User } from '../store/reducers/userReducer'
import {
  AlgorithmRun,
  WorkspaceConfig,
  WorkspaceDetails,
  setAlgorithmRunCsv,
  setAlgorithmRunListCsv,
  setSelectedWorkspace,
  setWorkspaceDataSourceListCsv,
  setWorkspaceDataStoreListCsv,
  setWorkspaceDetailsCsv,
  setWorkspaceDetailsLoading,
  setWorkspaceParticipantListCsv,
} from '../store/reducers/workspaceReducer'
import useDataSources from './useDataSources'
import useDataStores from './useDataStores'
import useDownloadFile from './useDownloadFile'
import usePermissions from './usePermissions'

interface useWorkspaceDetailsProps {
  workspaceId?: string
  preventFetch?: boolean
}

const useWorkspaceDetails = ({
  workspaceId,
  preventFetch = false,
}: useWorkspaceDetailsProps) => {
  const dispatch = useDispatch()
  const downloadCsv = useDownloadFile()

  const selectedWorkspace: WorkspaceDetails | undefined = useSelector(
    (state: RootState) => state.workspace.selected
  )

  const selectedAlgorithmRun: AlgorithmRun | undefined = useSelector(
    (state: RootState) => state.workspace.selectedAlgorithmRun
  )

  const workspaceDetailsLoading = useSelector(
    (state: RootState) => state.workspace.workspaceDetailsLoading
  )

  const [error, setError] = useState<AxiosError | Error>()

  const [isFetchDoneWorkspaceDetails, setIsFetchDoneWorkspaceDetails] =
    useState(false)

  const { getPermittedUsersOfDataSource } = useDataSources({
    preventFetch: true,
  })
  const { getPermittedUsersOfDataStore } = useDataStores({
    preventFetch: true,
  })

  const uiFlag = selectedWorkspace?.uiFlags ?? 0

  const {
    isDeletable,
    isUpdatable,
  } = usePermissions({
    uiFlag,
  })

  const fetchWorkspaceDetails = useCallback(
    (refreshForceCall = false) => {
      if (!workspaceId) {
        return
      }
      if (
        !workspaceDetailsLoading &&
        ((!preventFetch && !isFetchDoneWorkspaceDetails) || refreshForceCall)
      ) {
        dispatch(setWorkspaceDetailsLoading(true))
        workspaceService
          .getWorkspaceDetails({ workspaceId })
          .then((response: WorkspaceDetails) => {
            dispatch(setSelectedWorkspace(response))
            dispatch(setWorkspaceDetailsLoading(false))
            setIsFetchDoneWorkspaceDetails(true)
          })
          .catch((error: AxiosError | Error) => {
            dispatch(setWorkspaceDetailsLoading(false))

            setIsFetchDoneWorkspaceDetails(true)
            console.error('axios fetch error', error)
            setError(error)
          })
      }
    },
    [
      dispatch,
      workspaceId,
      workspaceDetailsLoading,
      preventFetch,
      isFetchDoneWorkspaceDetails,
    ]
  )

  const refreshWorkspaceDetails = useCallback(async () => {
    fetchWorkspaceDetails(true)
  }, [fetchWorkspaceDetails])

  useEffect(() => {
    fetchWorkspaceDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exportSmartFunctionCsv = useCallback(
    (workspaceId: string, algorithmRunId: string) => {
      smartFunctionService
        .exportRun({
          workspaceId,
          algorithmRunId,
        })
        .then((response: string) => {
          const filename = `${selectedWorkspace?.title}_${selectedWorkspace?.algorithmName}_${selectedAlgorithmRun?.completedDate}.csv`
          downloadCsv(response, filename)
          dispatch(setAlgorithmRunCsv(response))
        })
        .catch((error: AxiosError) => {
          notificationService.notificationError({
            messageToDisplay: error.message,
          })
        })
    },
    [selectedWorkspace, selectedAlgorithmRun, downloadCsv, dispatch]
  )

  const exportWorkspaceDetailsPanelCsv = useCallback(
    (workspaceId: string) => {
      if (selectedWorkspace) {
        workspaceService
          .exportWorkspaceDetails({
            workspaceId: workspaceId || selectedWorkspace.id,
          })
          .then((response: string) => {
            const filename = `${selectedWorkspace.title}_panel.csv`
            downloadCsv(response, filename)
            setWorkspaceDetailsCsv(response)
          })
          .catch((error: AxiosError) => {
            console.error(
              'Error fetching workspace details panel CSV data',
              error
            )
          })
      }
    },
    [selectedWorkspace, downloadCsv]
  )

  const exportWorkspaceDataSourcePanelCsv = useCallback(
    (workspaceId: string) => {
      if (
        selectedWorkspace &&
        workspaceId !== undefined &&
        workspaceId !== ''
      ) {
        workspaceService
          .exportWorkspaceDataSource({
            workspaceId: workspaceId || selectedWorkspace.id,
          })
          .then((response: string) => {
            const filename = `${selectedWorkspace.title}_data_source_panel.csv`
            downloadCsv(response, filename)
            setWorkspaceDataSourceListCsv(response)
          })
          .catch((error: AxiosError) => {
            console.error(
              'Error fetching workspace details panel CSV data',
              error
            )
          })
      }
    },
    [selectedWorkspace, downloadCsv]
  )

  const exportWorkspaceDataStorePanelCsv = useCallback(
    (workspaceId: string) => {
      if (
        selectedWorkspace &&
        workspaceId !== undefined &&
        workspaceId !== ''
      ) {
        workspaceService
          .exportWorkspaceDataStores({
            workspaceId: workspaceId || selectedWorkspace.id,
          })
          .then((response: string) => {
            const filename = `${selectedWorkspace.title}_data_store_panel.csv`
            downloadCsv(response, filename)
            setWorkspaceDataStoreListCsv(response)
          })
          .catch((error: AxiosError) => {
            console.error(
              'Error fetching workspace details panel CSV data',
              error
            )
          })
      }
    },
    [selectedWorkspace, downloadCsv]
  )

  const exportWorkspaceParticipantsPanelCsv = useCallback(
    (workspaceId: string) => {
      if (
        selectedWorkspace &&
        workspaceId !== undefined &&
        workspaceId !== ''
      ) {
        workspaceService
          .exportWorkspaceParticipants({
            workspaceId: workspaceId || selectedWorkspace.id,
          })
          .then((response: string) => {
            const filename = `${selectedWorkspace.title}_participants_panel.csv`
            downloadCsv(response, filename)
            setWorkspaceParticipantListCsv(response)
          })
          .catch((error: AxiosError) => {
            console.error(
              'Error fetching workspace details panel CSV data',
              error
            )
          })
      }
    },
    [selectedWorkspace, downloadCsv]
  )

  const exportWorkspaceAlgorithmRunsListCsv = useCallback(
    (workspaceId: string) => {
      if (
        selectedWorkspace &&
        workspaceId !== undefined &&
        workspaceId !== ''
      ) {
        workspaceService
          .exportWorkspaceAlgorithmRunsList({
            workspaceId,
          })
          .then((response: string) => {
            const filename = `${selectedWorkspace.title}_algorithm_runs.csv`
            downloadCsv(response, filename)
            setAlgorithmRunListCsv(response)
          })
          .catch((error: AxiosError) => {
            console.error(
              'Error fetching workspace algorithm runs list CSV data',
              error
            )
          })
      }
    },
    [downloadCsv, selectedWorkspace]
  )

  const getWorkspaceWorkflowTemplate = useCallback(async () => {
    if (selectedWorkspace) {
      workspaceService.getWorkspaceWorkflowTemplate({
        workspaceTypeId: selectedWorkspace.typeCode,
      })
    }
  }, [selectedWorkspace])

  const updateWorkspaceDetails = useCallback(
    async (config: WorkspaceConfig, notificationMessage?: string) => {
      if (!selectedWorkspace) {
        console.error('No selected workspace')
        return
      }

      workspaceService
        .updateWorkspaceDetails({
          workspaceId: selectedWorkspace.id,
          config,
        })
        .then(() => {
          refreshWorkspaceDetails()
          if (notificationMessage) {
            notificationService.notificationSuccess({
              messageToDisplay: notificationMessage,
            })
          }
        })
        .catch((error: AxiosError) => {
          console.error('Failed to update workspace details:', error)
        })
    },
    [selectedWorkspace, refreshWorkspaceDetails]
  )

  const getPermittedUsersOfWorkspace = useCallback(async () => {
    if (selectedWorkspace) {
      const [users, dataStoreUsers] = await Promise.all([
        getPermittedUsersOfDataSource({
          dataSourceId: selectedWorkspace.dataSource.id,
        }),
        selectedWorkspace.dataStores && selectedWorkspace.dataStores.length > 0
          ? getPermittedUsersOfDataStore({
              dataStoreId: selectedWorkspace.dataStores[0].id,
            })
          : Promise.resolve([]),
      ])

      // get the set of users that are common to both
      const commonUsers = users.filter((user: User) =>
        dataStoreUsers.some(
          (dataStoreUser: User) => dataStoreUser.id === user.id
        )
      )
      return commonUsers.map((user: User) => user.id) || []
    }
    return []
  }, [
    selectedWorkspace,
    getPermittedUsersOfDataSource,
    getPermittedUsersOfDataStore,
  ])

  return {
    error,
    exportSmartFunctionCsv,
    exportWorkspaceDataSourcePanelCsv,
    exportWorkspaceDataStorePanelCsv,
    exportWorkspaceDetailsPanelCsv,
    exportWorkspaceParticipantsPanelCsv,
    exportWorkspaceAlgorithmRunsListCsv,
    fetchWorkspaceDetails,
    refreshWorkspaceDetails,
    selectedWorkspace,
    workspaceDetailsLoading,
    getWorkspaceWorkflowTemplate,
    updateWorkspaceDetails,
    getPermittedUsersOfWorkspace,
    uiFlag,
    isDeletable,
    isUpdatable,
  }
}

export default useWorkspaceDetails
